// /frontend/dashboard/src/router/modules/robotRoutes.js
// Robot route module for robot-centric dashboard pages.
// English explanations are followed by Thai explanations for route-design validation and debugging.

import { debugLog } from '../../utils/debugGate.js';

const ROUTE_DEBUG_NAMESPACE = '[router/robotRoutes]';

function emitRobotRouteDebug(label, payload) {
  debugLog(label, payload);
}

// Static import registry — Vite can statically analyze these at build time.
// English: All component imports must be literal string paths inside this registry.
//          Never use runtime variable strings inside import() — Vite cannot bundle them.
// ไทย: import ทุกตัวต้องเป็น literal string path ใน registry นี้
//      ห้ามใช้ variable string ใน import() เพราะ Vite จะไม่สร้าง chunk สำหรับ production
const PAGE_REGISTRY = {
  DashboardPage:    () => import('../../pages/DashboardPage.vue'),
  // English: RobotListPage — full robot list with inline edit (name, site dropdown).
  // ไทย: RobotListPage — รายชื่อ robot ทั้งหมด พร้อม edit ชื่อและ site แบบ inline
  RobotListPage:    () => import('../../pages/RobotListPage.vue'),
  // English: AddRobotPage must be in the static registry so Vite can bundle it.
  // ไทย: AddRobotPage ต้องอยู่ใน static registry เพื่อให้ Vite สร้าง chunk ได้ถูกต้อง
  AddRobotPage:     () => import('../../pages/AddRobotPage.vue'),
  // English: RobotApiKeysPage — admin UI for generating and revoking per-robot API keys.
  //          Must be declared before /:robotId to prevent 'api-keys' being captured as a param.
  // ไทย: RobotApiKeysPage — UI สำหรับ admin สร้าง/ยกเลิก API key ต่อ robot
  //      ต้องประกาศก่อน /:robotId เพื่อป้องกัน 'api-keys' ถูก capture เป็น param
  RobotApiKeysPage:    () => import('../../pages/RobotApiKeysPage.vue'),
  HardwareMonitorPage: () => import('../../pages/HardwareMonitorPage.vue'),
  // English: RobotTransitConfigPage — GPS coordinate config for zone entry + dock.
  //          Must be declared before /:robotId to prevent 'transit-config' captured as param.
  // ไทย: RobotTransitConfigPage — ตั้งค่าพิกัด GPS สำหรับ zone entry และ dock
  //      ต้องอยู่ก่อน /:robotId เพื่อป้องกัน 'transit-config' ถูก capture เป็น param
  RobotTransitConfigPage: () => import('../../pages/RobotTransitConfigPage.vue'),
};

function buildLazyPage(registryKey, debugLabel) {
  return async () => {
    // [Debug output disabled — freeze/performance isolation | ปิด debug output ชั่วคราวเพื่อแยกปัญหา freeze/performance]
    // emitRobotRouteDebug(
    //   `${ROUTE_DEBUG_NAMESPACE} [LAZY_IMPORT_START] English: starting lazy import for robot route component | ไทย: เริ่ม lazy import สำหรับ component ของ robot route`,
    //   {
    //     registryKey,
    //     debugLabel,
    //   },
    // );

    try {
      const loader = PAGE_REGISTRY[registryKey];

      if (!loader) {
        throw new Error(
          `${ROUTE_DEBUG_NAMESPACE} No registry entry for key: ${registryKey}. Add a static import() entry to PAGE_REGISTRY.`,
        );
      }

      const module = await loader();

      // [Debug output disabled — freeze/performance isolation | ปิด debug output ชั่วคราวเพื่อแยกปัญหา freeze/performance]
      // emitRobotRouteDebug(
      //   `${ROUTE_DEBUG_NAMESPACE} [LAZY_IMPORT_SUCCESS] English: robot route component loaded successfully | ไทย: โหลด component ของ robot route สำเร็จ`,
      //   {
      //     registryKey,
      //     debugLabel,
      //     exportKeys: Object.keys(module || {}),
      //     hasDefaultExport: Boolean(module && module.default),
      //   },
      // );

      return module;
    } catch (error) {
      console.error(
        `${ROUTE_DEBUG_NAMESPACE} [LAZY_IMPORT_ERROR] English: failed to load robot route component | ไทย: โหลด component ของ robot route ไม่สำเร็จ`,
        {
          registryKey,
          debugLabel,
          error: error instanceof Error ? error.message : String(error),
        },
      );
      // English: Chunk hash mismatch after a new deploy — old HTML references a stale chunk
      //          filename that no longer exists on the server. Reload once to pick up the
      //          new index.html and fresh chunk filenames.
      // Thai: chunk hash ไม่ตรงหลัง deploy ใหม่ — HTML เก่าอ้างถึง chunk ที่ไม่มีบน server แล้ว
      //       reload ครั้งเดียวเพื่อให้ browser ดึง index.html และ chunk ใหม่
      if (typeof window !== 'undefined' && !sessionStorage.getItem('_chunk_reload')) {
        sessionStorage.setItem('_chunk_reload', '1');
        window.location.reload();
      }
      throw error;
    }
  };
}

function createRobotMeta(meta = {}) {
  const merged = {
    section: 'robot',
    requiresAuth: true,
    requiresAdmin: false,
    layout: 'dashboard',
    navGroup: 'robot',
    debug_en: 'Robot route metadata helps validate whether robot-centric pages are grouped correctly and remain separate from dashboard home, mission pages, and admin-only routes.',
    debug_th: 'metadata ของ robot route ใช้ตรวจว่าหน้าที่เกี่ยวกับหุ่นยนต์ถูกจัดกลุ่มอย่างถูกต้องและแยกจาก dashboard หลัก, หน้า mission และหน้าที่เป็น admin-only หรือไม่',
    ...meta,
  };

  // [Debug output disabled — freeze/performance isolation | ปิด debug output ชั่วคราวเพื่อแยกปัญหา freeze/performance]
  // emitRobotRouteDebug(
  //   `${ROUTE_DEBUG_NAMESPACE} [META_CREATED] English: created robot route metadata | ไทย: สร้าง metadata ของ robot route แล้ว`,
  //   merged,
  // );

  return merged;
}

const robotRoutes = [
  {
    path: '/robots',
    redirect: '/robots/overview',
    meta: createRobotMeta({
      title: 'Robot Root',
      navLabel: 'Robots',
      routePurpose_en: 'Robot root route redirects to the robot overview page.',
      routePurpose_th: 'route หลักของ robot จะ redirect ไปยังหน้า robot overview',
    }),
  },
  {
    path: '/robots/overview',
    name: 'RobotOverview',
    component: buildLazyPage('RobotListPage', 'RobotListPage'),
    meta: createRobotMeta({
      title: 'Robots',
      navLabel: 'Overview',
      permissions: ['platform_admin', 'tenant_admin', 'site_operator', 'viewer'],
      routePurpose_en: 'Robot overview — full list of registered robots with inline edit for name and site.',
      routePurpose_th: 'Robot overview — รายชื่อ robot ทั้งหมด พร้อม edit ชื่อและ site แบบ inline',
    }),
  },
  {
    path: '/robots/live',
    name: 'RobotLive',
    component: buildLazyPage('DashboardPage', 'RobotLiveViaDashboardPage'),
    meta: createRobotMeta({
      title: 'Robot Live',
      navLabel: 'Live',
      permissions: ['platform_admin', 'tenant_admin', 'site_operator'],
      routePurpose_en: 'Robot live route focuses on validating realtime robot telemetry, robot status changes, and map synchronization.',
      routePurpose_th: 'route ของ robot live ใช้ตรวจ telemetry แบบ realtime, การเปลี่ยนสถานะของหุ่นยนต์ และการ sync กับแผนที่',
      aliasOf: 'DashboardHome',
    }),
  },
  {
    // English: API Keys route must be declared before /:robotId — admin-only page for
    //          generating/revoking per-robot machine-to-machine API keys.
    // ไทย: route API Keys ต้องอยู่ก่อน /:robotId — หน้า admin สำหรับสร้าง/ยกเลิก API key ต่อ robot
    path: '/robots/api-keys',
    name: 'RobotApiKeys',
    component: buildLazyPage('RobotApiKeysPage', 'RobotApiKeysPage'),
    meta: createRobotMeta({
      title: 'Robot API Keys',
      navLabel: 'API Keys',
      requiresAdmin: true,
      permissions: ['platform_admin', 'tenant_admin'],
      routePurpose_en: 'Admin page for generating and revoking machine-to-machine API keys per robot. Each Jetson gets one key stored in ROBOT_API_KEY env var.',
      routePurpose_th: 'หน้า admin สำหรับสร้างและยกเลิก API key แบบ machine-to-machine ต่อ robot แต่ละตัว แต่ละ Jetson ได้ key หนึ่งตัวเก็บใน ROBOT_API_KEY env var',
    }),
  },
  {
    // English: HardwareMonitor — admin view of devices detected on each Jetson Nano.
    //          Must be declared before /:robotId to prevent 'hardware-monitor' being captured as a param.
    // ไทย: HardwareMonitor — admin view อุปกรณ์ที่ตรวจพบบน Jetson Nano แต่ละตัว
    //      ต้องอยู่ก่อน /:robotId เพื่อป้องกัน 'hardware-monitor' ถูก capture เป็น param
    path: '/robots/hardware-monitor',
    name: 'HardwareMonitor',
    component: buildLazyPage('HardwareMonitorPage', 'HardwareMonitorPage'),
    meta: createRobotMeta({
      title: 'Hardware Monitor',
      navLabel: 'Hardware Monitor',
      requiresAdmin: true,
      permissions: ['platform_admin', 'tenant_admin'],
      routePurpose_en: 'Admin page showing USB, I2C, serial, camera, and ROS2 topic snapshots from each Jetson Nano.',
      routePurpose_th: 'หน้า admin แสดง USB, I2C, serial, camera และ ROS2 topic snapshot จาก Jetson Nano แต่ละตัว',
    }),
  },
  {
    // English: Transit Config route — GPS coordinates for zone entry and dock.
    //          Must be before /:robotId to prevent 'transit-config' captured as param.
    // ไทย: Transit Config route — พิกัด GPS สำหรับ zone entry และ dock
    //      ต้องอยู่ก่อน /:robotId เพื่อป้องกัน 'transit-config' ถูก capture เป็น param
    path: '/robots/transit-config',
    name: 'RobotTransitConfig',
    component: buildLazyPage('RobotTransitConfigPage', 'RobotTransitConfigPage'),
    meta: createRobotMeta({
      title: 'Transit Config',
      navLabel: 'Transit Config',
      permissions: ['platform_admin', 'tenant_admin', 'site_operator'],
      routePurpose_en: 'Transit Config page — operators paste Google Maps GPS coordinates for zone entry and dock. Saved to DB with history, pushed to Jetson via MQTT.',
      routePurpose_th: 'หน้า Transit Config — operator วางพิกัด GPS จาก Google Maps สำหรับ zone entry และ dock บันทึกลง DB พร้อมประวัติ ส่งไป Jetson ผ่าน MQTT',
    }),
  },
  {
    // English: AddRobot route must be declared before /:robotId to prevent 'add' being
    //          captured as a robotId param. Accepts optional query: tenant_id, site_id
    //          to enable context mode when opened from Create Site success flow.
    // ไทย: route AddRobot ต้องอยู่ก่อน /:robotId เพื่อป้องกัน 'add' ถูก capture เป็น robotId param
    //      รับ query: tenant_id, site_id ได้ (optional) สำหรับ context mode จาก Create Site
    path: '/robots/add',
    name: 'AddRobot',
    component: buildLazyPage('AddRobotPage', 'AddRobotPageComponent'),
    meta: createRobotMeta({
      title: 'Add Robot',
      navLabel: 'Add Robot',
      permissions: ['platform_admin', 'tenant_admin', 'site_operator'],
      routePurpose_en: 'Add Robot route allows registering a new robot. Supports context mode when tenant_id and site_id are provided as query params from the Create Site success flow.',
      routePurpose_th: 'route Add Robot ใช้ลงทะเบียน robot ใหม่ รองรับ context mode เมื่อ tenant_id และ site_id ถูกส่งมาเป็น query params จาก Create Site success flow',
    }),
  },
  {
    path: '/robots/:robotId',
    name: 'RobotDetail',
    component: buildLazyPage('DashboardPage', 'RobotDetailViaDashboardPage'),
    props: true,
    meta: createRobotMeta({
      title: 'Robot Detail',
      navLabel: 'Detail',
      permissions: ['platform_admin', 'tenant_admin', 'site_operator', 'viewer'],
      routePurpose_en: 'Robot detail route is reserved for robot-specific validation while temporarily reusing the main dashboard page.',
      routePurpose_th: 'route ของ robot detail ถูกเตรียมไว้สำหรับการตรวจเฉพาะหุ่นยนต์รายตัว โดยชั่วคราวยังใช้หน้า dashboard หลักร่วมกัน',
      aliasOf: 'DashboardHome',
      expectsRobotParam: true,
    }),
  },
];

// [Debug output disabled — freeze/performance isolation | ปิด debug output ชั่วคราวเพื่อแยกปัญหา freeze/performance]
// emitRobotRouteDebug(
//   `${ROUTE_DEBUG_NAMESPACE} [ROUTES_REGISTERED] English: robot route module initialized | ไทย: module ของ robot routes ถูกเตรียมใช้งานแล้ว`,
//   {
//     routeCount: robotRoutes.length,
//     routeNames: robotRoutes.map((route) => route.name || `redirect:${route.path}`),
//     paths: robotRoutes.map((route) => route.path),
//     registryKeys: Object.keys(PAGE_REGISTRY),
//     explanation_en: 'Use this snapshot to verify that robot routes are grouped separately from dashboard, admin, mission, and alert route modules.',
//     explanation_th: 'ใช้ snapshot นี้ตรวจว่า robot routes ถูกแยกออกจาก dashboard, admin, mission และ alert route modules อย่างถูกต้อง',
//   },
// );

export default robotRoutes;
